import {
  createOutcomeSuccess,
  deleteCampaignRuleOutcome,
  fetchAllCampaignRuleOutcomes,
  fetchAllCampaignRuleOutcomesFailure,
  fetchAllCampaignRuleOutcomesRequest,
  fetchAllCampaignRuleOutcomesSuccess,
  fetchFilterCampaignRuleOutcomes,
  fetchSingleRuleSuccess,
  receiveRuleOutcomes,
  removeRuleOutcome,
  showErrorAlert,
  updateCampaignRuleOutcome,
  updateCampaignRuleOutcomeConfig,
  updateCampaignRuleOutcomeConfigFailure,
  updateCampaignRuleOutcomeConfigRequest,
  updateCampaignRuleOutcomeConfigSuccess,
} from 'actions';
import { apiClient as LitLingoClient } from 'client';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import type {
  API,
  CampaignRuleOutcome,
  MOutcome,
  MRule,
  NormalizedResource,
  SagaReturn,
  UUID,
} from 'types';

function* fetchAllCampaignRuleOutcomesSaga({
  payload,
}: ReturnType<typeof fetchAllCampaignRuleOutcomes>): SagaReturn {
  yield put(fetchAllCampaignRuleOutcomesRequest());

  const response = (yield call([LitLingoClient.resources.campaignRuleOutcomes, 'list'], {
    params: { ...payload, relationships: ['campaign', 'outcome', 'rule'] },
  })) as API.Response<API.CampaignRuleOutcomes.List>;
  if (response.error != null) {
    yield put(fetchAllCampaignRuleOutcomesFailure(response.error));
  } else {
    const normalizedRuleOutcome: NormalizedResource<CampaignRuleOutcome> = {};
    response.data.records.forEach((ruleOutcome) => {
      normalizedRuleOutcome[ruleOutcome.uuid] = ruleOutcome;
    });
    yield put(fetchAllCampaignRuleOutcomesSuccess(normalizedRuleOutcome));
  }
}

function* fetchFilterCampaignRuleOutcomesSaga({
  payload,
}: ReturnType<typeof fetchFilterCampaignRuleOutcomes>): SagaReturn {
  yield put(fetchAllCampaignRuleOutcomesRequest());

  const response = (yield call([LitLingoClient.resources.campaignRuleOutcomes, 'list'], {
    params: { ...payload, selectable_fields: ['uuid', 'rule_uuid', 'campaign.[deleted_at, name]'] },
  })) as API.Response<API.CampaignRuleOutcomes.List>;
  if (response.error != null) {
    yield put(fetchAllCampaignRuleOutcomesFailure(response.error));
  } else {
    const normalizedRuleOutcome: NormalizedResource<CampaignRuleOutcome> = {};
    response.data.records.forEach((ruleOutcome) => {
      normalizedRuleOutcome[ruleOutcome.uuid] = ruleOutcome;
    });
    yield put(fetchAllCampaignRuleOutcomesSuccess(normalizedRuleOutcome));
  }
}

function* updateRuleOutcomeConfigSaga({
  payload,
}: ReturnType<typeof updateCampaignRuleOutcomeConfig>): SagaReturn {
  yield put(updateCampaignRuleOutcomeConfigRequest(payload));
  const response = (yield call([LitLingoClient.resources.campaignRuleOutcomes, 'upsert'], {
    data: payload,
  })) as API.Response<API.CampaignRuleOutcomes.Upsert>;
  if (response.error != null) {
    yield put(updateCampaignRuleOutcomeConfigFailure(response.error));
  } else {
    yield put(updateCampaignRuleOutcomeConfigSuccess());
  }
}

type CreateRuleOutcomePayload = {
  ruleId: UUID;
  campaignId: UUID;
  outcomeId: UUID;
};

export function* createRuleOutcome(
  payload: CreateRuleOutcomePayload
): SagaReturn<boolean | CampaignRuleOutcome> {
  const { ruleId, outcomeId, campaignId } = payload;
  const ruleOutcome = {
    rule_uuid: ruleId,
    outcome_uuid: outcomeId,
    campaign_uuid: campaignId,
  };

  const response = (yield call([LitLingoClient.resources.campaigns.extras, 'addRuleOutcome'], {
    urlParams: { campaignId: ruleOutcome.campaign_uuid },
    data: ruleOutcome,
  })) as API.Response<API.Campaigns.AddRuleOutcome>;

  if (response.error != null) {
    return false;
  }

  yield put(
    updateCampaignRuleOutcome({
      ruleId: response.data.rule_uuid,
      campaignId: response.data.campaign_uuid,
    })
  );
  return response.data;
}

function* removeRuleOutcomeSaga(action: ReturnType<typeof removeRuleOutcome>): SagaReturn {
  const { payload } = action;
  const { ruleId, outcomeId, campaignId } = payload;
  const data = {
    rule_uuid: ruleId,
    outcome_uuid: outcomeId,
  };
  const response = (yield call([LitLingoClient.resources.campaigns.extras, 'removeRuleOutcome'], {
    urlParams: { campaignId },
    data,
  })) as API.Response<API.Campaigns.RemoveRuleOutcome>;
  if (response.error != null) {
    yield put(showErrorAlert(response.error.message));
  } else {
    yield put(deleteCampaignRuleOutcome({ ruleId, outcomeId, campaignId }));
  }
}

function* receiveRuleOutcomesSaga(action: ReturnType<typeof receiveRuleOutcomes>): SagaReturn {
  const { payload } = action;
  const normalizedRule: NormalizedResource<MRule> = {};
  const normalizedOutcome: NormalizedResource<MOutcome> = {};
  const normalizedRuleOutcome: NormalizedResource<CampaignRuleOutcome> = {};

  yield all(
    payload.map((payloadObj) => {
      const { rule, outcome, ...ruleOutcomeBody } = payloadObj;
      normalizedRuleOutcome[ruleOutcomeBody.uuid] = ruleOutcomeBody;

      if (outcome != null) {
        normalizedOutcome[outcome.uuid] = {
          ...outcome,
          campaign_uuid: ruleOutcomeBody.campaign_uuid,
          ruleOutcome_uuid: ruleOutcomeBody.uuid,
        };
        if (rule != null) {
          const { ...ruleBody } = rule;
          if (rule.uuid in normalizedRule) {
            normalizedRule[rule.uuid].outcomes = [
              ...(normalizedRule[rule.uuid].outcomes ?? []),
              outcome.uuid,
            ];
          } else {
            normalizedRule[rule.uuid] = {
              ...ruleBody,
              campaign_uuid: ruleOutcomeBody.campaign_uuid,
              ruleOutcome_uuid: ruleOutcomeBody.uuid,
              outcomes: [outcome.uuid],
            } as MRule;
          }
          return put(fetchSingleRuleSuccess(normalizedRule[rule.uuid]));
        }
      }

      return null;
    })
  );

  yield put(fetchAllCampaignRuleOutcomesSuccess(normalizedRuleOutcome));
  yield put(createOutcomeSuccess({ outcome: normalizedOutcome }));
}

function* campaignRuleOutcomesSaga(): SagaReturn {
  yield takeLatest(fetchAllCampaignRuleOutcomes.toString(), fetchAllCampaignRuleOutcomesSaga);
  yield takeLatest(updateCampaignRuleOutcomeConfig.toString(), updateRuleOutcomeConfigSaga);
  yield takeLatest(removeRuleOutcome.toString(), removeRuleOutcomeSaga);
  yield takeLatest(receiveRuleOutcomes.toString(), receiveRuleOutcomesSaga);
  yield takeLatest(fetchFilterCampaignRuleOutcomes.toString(), fetchFilterCampaignRuleOutcomesSaga);
}

export default campaignRuleOutcomesSaga;
